import { __rest } from "tslib";
import merge from 'lodash/merge';
import lodashIntersection from 'lodash/intersection';
import lodashOrderBy from 'lodash/orderBy';
import cloneDeep from 'lodash/cloneDeep';
import { api } from '@/api/api';
import { apis } from '@/config';
import { ContactServerInstance } from './contact_server';
import { util } from '@/api/util';
import { ContactDBInstance } from './contact_dbl';
import ContactUtilInterface from './contact_util';
import { ContactTransformInstance } from './contact_transform';
import { writeOrgsToLoki, writeOrgContactsToLoki, deleteOrgContactsByOrgId } from './contact_sync_enterprise_loki';
import { contactSearchDbName } from './util/const';
export class ContactPersonalHelper {
    constructor() {
        this.systemApi = api.getSystemApi();
        this.contactServer = ContactServerInstance;
        this.contactDB = ContactDBInstance;
        this.contactUtil = ContactUtilInterface;
        this.contactTrans = ContactTransformInstance;
    }
    getLokiApi() {
        return api.requireLogicalApi(apis.dbMemoryApiImpl);
    }
    getDbAccount(_account) {
        return _account || this.contactUtil.getCurrentAccount();
    }
    async updatePersonalOrgInLoki(contactIds, orgIdList, action, _account) {
        const lokiApi = this.getLokiApi();
        const dbAccount = this.getDbAccount(_account);
        const contacts = await lokiApi.getByRangeCondition({
            dbName: contactSearchDbName,
            tableName: 'contact',
            adCondition: { type: 'anyOf', field: 'id', args: [contactIds] },
            _dbAccount: dbAccount,
        });
        if (!(contacts === null || contacts === void 0 ? void 0 : contacts.length)) {
            return;
        }
        const updated = contacts.map(contact => {
            const info = contact.info || {};
            const current = Array.isArray(info.personalOrg) ? info.personalOrg : Array.isArray(contact.personalOrg) ? contact.personalOrg : [];
            let next;
            if (action === 'insert') {
                next = [...new Set([...current, ...orgIdList])];
            }
            else {
                next = current.filter(id => !orgIdList.includes(id));
            }
            return Object.assign(Object.assign({}, contact), { info: Object.assign(Object.assign({}, info), { personalOrg: next }) });
        });
        await lokiApi.putAll({ dbName: contactSearchDbName, tableName: 'contact', _dbAccount: dbAccount }, updated);
    }
    async deleteContactsFromLoki(contactIds, _account) {
        if (!contactIds.length) {
            return;
        }
        const lokiApi = this.getLokiApi();
        const dbAccount = this.getDbAccount(_account);
        await lokiApi.deleteByByRangeCondition({
            dbName: contactSearchDbName,
            tableName: 'contact',
            adCondition: { type: 'anyOf', field: 'id', args: [contactIds] },
            _dbAccount: dbAccount,
        });
    }
    async deleteOrgsFromLoki(orgIds, _account) {
        if (!orgIds.length) {
            return;
        }
        const lokiApi = this.getLokiApi();
        const dbAccount = this.getDbAccount(_account);
        await lokiApi.deleteByByRangeCondition({
            dbName: contactSearchDbName,
            tableName: 'org',
            adCondition: { type: 'anyOf', field: 'id', args: [orgIds] },
            _dbAccount: dbAccount,
        });
    }
    async deleteOrgContactsByOrgIds(orgIds, _account) {
        if (!orgIds.length) {
            return;
        }
        const lokiApi = this.getLokiApi();
        const dbAccount = this.getDbAccount(_account);
        await Promise.all(orgIds.map(orgId => deleteOrgContactsByOrgId(lokiApi, orgId, dbAccount)));
    }
    async updateMarkedInLoki(ids, type, _account) {
        const lokiApi = this.getLokiApi();
        const dbAccount = this.getDbAccount(_account);
        const tableName = type === 1 ? 'contact' : 'org';
        const records = await lokiApi.getByRangeCondition({
            dbName: contactSearchDbName,
            tableName,
            adCondition: { type: 'anyOf', field: 'id', args: [[...ids.keys()]] },
            _dbAccount: dbAccount,
        });
        if (!(records === null || records === void 0 ? void 0 : records.length)) {
            return;
        }
        const updated = records.map(r => {
            var _a, _b;
            const _c = r, { $loki, meta } = _c, rest = __rest(_c, ["$loki", "meta"]);
            return Object.assign(Object.assign({}, rest), { marked: (_b = (_a = ids.get(r.id)) === null || _a === void 0 ? void 0 : _a.marked) !== null && _b !== void 0 ? _b : r.marked });
        });
        await lokiApi.putAll({ dbName: contactSearchDbName, tableName, _dbAccount: dbAccount }, updated);
    }
    async writePesonalMarkToLoki(list, _account) {
        if (!list.length) {
            return;
        }
        const lokiApi = this.getLokiApi();
        const dbAccount = this.getDbAccount(_account);
        const listWithId = list.map(item => (Object.assign(Object.assign({}, item), { id: util.getUnique(item.value) })));
        await lokiApi.putAll({ dbName: contactSearchDbName, tableName: this.contactUtil.personalMarkTable, _dbAccount: dbAccount }, listWithId);
    }
    async deletePersonalMarkFromLoki(values, _account) {
        if (!values.length) {
            return;
        }
        const lokiApi = this.getLokiApi();
        const dbAccount = this.getDbAccount(_account);
        await lokiApi.deleteByByRangeCondition({
            dbName: contactSearchDbName,
            tableName: this.contactUtil.personalMarkTable,
            adCondition: { type: 'anyOf', field: 'value', args: [values] },
            _dbAccount: dbAccount,
        });
    }
    async getMaxPersonalMarkFromLoki(_account) {
        const lokiApi = this.getLokiApi();
        const dbAccount = this.getDbAccount(_account);
        const list = (await lokiApi.getByEqCondition({
            dbName: contactSearchDbName,
            tableName: this.contactUtil.personalMarkTable,
            _dbAccount: dbAccount,
        }));
        const markedList = (list || []).map(item => item.marked);
        markedList.push(0);
        return {
            count: Math.max(...markedList),
            data: list || [],
        };
    }
    transOrgIdByPersonalOrg(id) {
        if (id.startsWith('personal_org_')) {
            return id;
        }
        return 'personal_org_' + id;
    }
    async doGetPersonalOrg(params) {
        const { _account = this.contactUtil.getCurrentAccount(), idList = [] } = params;
        try {
            const lokiApi = this.getLokiApi();
            const dbAccount = this.getDbAccount(_account);
            let adCondition;
            if (Array.isArray(idList) && idList.length) {
                adCondition = { type: 'anyOf', field: 'id', args: [idList] };
            }
            else {
                adCondition = {
                    type: 'startsWith',
                    field: 'id',
                    args: ['personal_org'],
                };
            }
            const data = await lokiApi.getByRangeCondition({
                dbName: contactSearchDbName,
                tableName: 'org',
                adCondition,
                _dbAccount: dbAccount,
            });
            return {
                success: true,
                data: data || [],
            };
        }
        catch (e) {
            console.error('[contact] doGetPersonalOrg', e);
            return {
                success: false,
            };
        }
    }
    async doGetPersonalContact(option) {
        try {
            option = option || {};
            const { showDisable = false, isIM, _account = this.contactUtil.getCurrentAccount() } = option;
            const dbAccount = this.getDbAccount(_account);
            const memoryModels = await this.contactDB.getPersonalContactsFromMemory(this.contactUtil.StaticNodeKey.PERSON_ALL, dbAccount);
            if (!memoryModels || !memoryModels.length) {
                return [];
            }
            let contactModelList = memoryModels.map(item => {
                const model = this.contactDB.transformMemoryModel2StandardModel(item);
                model._account = _account;
                return model;
            });
            if (!showDisable) {
                contactModelList = contactModelList.filter(m => m.contact.visibleCode === 0 || m.contact.visibleCode === undefined);
            }
            if (isIM) {
                contactModelList = contactModelList.filter(m => m.contact.enableIM);
            }
            contactModelList = lodashOrderBy(contactModelList, ['contact.contactLabel', 'contact.contactPYName', 'contact.contactName'], ['asc', 'asc', 'asc']);
            return contactModelList;
        }
        catch (e) {
            console.warn('[contact] doGetPersonalContact from loki', e);
            return [];
        }
    }
    async doInsertPersonalOrg(params) {
        var _a;
        const { isMark, groupName: name, _account } = params;
        try {
            let marked;
            if (isMark) {
                const { count: maxMarkedNum } = await this.getMaxPersonalMarkFromLoki(_account);
                marked = maxMarkedNum + 1;
            }
            const { data, success, message } = await this.contactServer.insertPersonalOrg({
                groupName: name,
                idList: params.idList || [],
                marked,
                _account,
            });
            if (success && data) {
                const orgList = this.contactDB.personalOrgIntoOrg([data.personContactGroup]);
                const orgIdList = [this.transOrgIdByPersonalOrg(data.personContactGroup.groupId)];
                const promiseList = [];
                if (marked && data.personContactGroup.groupId) {
                    await Promise.all([
                        this.writePesonalMarkToLoki([
                            {
                                type: 2,
                                value: data.personContactGroup.groupId,
                                originId: data.personContactGroup.groupId,
                                name,
                                emails: [],
                                marked,
                                _lastUpdateTime: Date.now(),
                            },
                        ], _account),
                        this.contactDB
                            .doInsertPersonalMark({
                            list: [
                                {
                                    type: 2,
                                    value: data.personContactGroup.groupId,
                                    originId: data.personContactGroup.groupId,
                                    name,
                                    emails: [],
                                    marked,
                                },
                            ],
                            _account,
                        }, {
                            isIncrease: true,
                            _lastUpdateTime: Date.now(),
                            noNewMarkData: true,
                        })
                            .catch(e => console.error('[contact] doInsertPersonalOrg dexie mark error', e)),
                    ]);
                }
                if (orgList === null || orgList === void 0 ? void 0 : orgList.length) {
                    promiseList.push(Promise.all([
                        writeOrgsToLoki(this.getLokiApi(), orgList, this.getDbAccount(_account)),
                        this.contactDB
                            .intoTable({
                            tableName: this.contactUtil.orgTable,
                            list: orgList,
                            _account,
                        }, { asSoon: true })
                            .catch(e => console.error('[contact] doInsertPersonalOrg dexie org error', e)),
                    ])
                        .then(() => this.handlePersonalOrgSyncRes({
                        action: 'insert',
                        success: true,
                        idList: orgIdList,
                    }))
                        .catch(() => this.handlePersonalOrgSyncRes({
                        action: 'insert',
                        success: false,
                        idList: orgIdList,
                    })));
                }
                if ((_a = data.accountIdList) === null || _a === void 0 ? void 0 : _a.length) {
                    const personalOrgContact = this.contactTrans.transPersonalOrgContact(data.accountIdList, orgIdList);
                    promiseList.push(Promise.all([
                        writeOrgContactsToLoki(this.getLokiApi(), personalOrgContact, this.getDbAccount(_account)),
                        this.contactDB
                            .intoTable({
                            tableName: this.contactUtil.orgContactTable,
                            list: personalOrgContact,
                            _account,
                        }, { asSoon: true })
                            .catch(e => console.error('[contact] doInsertPersonalOrg dexie orgContact error', e)),
                    ])
                        .then(() => this.handlePersonalOrgContactSyncRes({
                        success: true,
                        action: 'insert',
                        idList: util.getKeyListByList(personalOrgContact, 'id'),
                    }))
                        .catch(() => this.handlePersonalOrgContactSyncRes({
                        success: false,
                        action: 'insert',
                        idList: util.getKeyListByList(personalOrgContact, 'id'),
                    })));
                    promiseList.push(Promise.all([
                        this.updatePersonalOrgInLoki(data.accountIdList, orgIdList, 'insert', _account),
                        this.contactDB
                            .updateContactById({
                            list: data.accountIdList.map(id => ({
                                id,
                                personalOrg: orgIdList,
                            })),
                            action: 'insert',
                            _account,
                        })
                            .catch(e => console.error('[contact] doInsertPersonalOrg dexie contact error', e)),
                    ])
                        .then(() => this.handlePersonalSyncRes({
                        action: 'update',
                        success: true,
                        idList: data.accountIdList,
                    }))
                        .catch(() => this.handlePersonalSyncRes({
                        action: 'update',
                        success: false,
                        idList: data.accountIdList,
                    })));
                }
                const arr = await Promise.all(promiseList);
                const { flag, resData } = this.handleSyncRes(arr);
                if (flag) {
                    this.contactDB.sendContactNotify(Object.assign(Object.assign({}, resData), { _account }));
                    return {
                        success: true,
                        data: {
                            id: this.transOrgIdByPersonalOrg(data.personContactGroup.groupId),
                            name: data.personContactGroup.groupName,
                        },
                    };
                }
            }
            return {
                success: false,
                message,
            };
        }
        catch (e) {
            console.error('[contact] doInsertPersonalOrg error', e);
            return {
                success: false,
            };
        }
    }
    async doUpdatePersonalOrg(params) {
        const { orgId, groupName: name, idList, isMark = false, _account } = params;
        try {
            let syncResArr = [];
            let marked;
            const { count: maxMarkedNum, data: markList } = await this.getMaxPersonalMarkFromLoki(_account);
            const markMap = new Map(markList.filter(item => item.type === 2).map(item => [item.value, item.marked]));
            if (isMark && !markMap.has(orgId)) {
                marked = maxMarkedNum + 1;
            }
            if (!isMark && markMap.has(orgId)) {
                marked = -100;
            }
            if (isMark && markMap.has(orgId)) {
                marked = markMap.get(orgId);
            }
            const { data, success } = await this.contactServer.updatePersonalOrg({
                orgId,
                groupName: name,
                idList,
                marked,
                _account: params._account,
            });
            if (success && data) {
                if (typeof marked === 'number') {
                    await Promise.all([
                        this.writePesonalMarkToLoki([
                            {
                                type: 2,
                                value: orgId,
                                name,
                                emails: [],
                                marked,
                                _lastUpdateTime: Date.now(),
                            },
                        ], _account),
                        this.contactDB
                            .doInsertPersonalMark({
                            list: [
                                {
                                    type: 2,
                                    value: orgId,
                                    name,
                                    emails: [],
                                    marked,
                                },
                            ],
                            _account,
                        }, {
                            isIncrease: true,
                            _lastUpdateTime: Date.now(),
                            noNewMarkData: true,
                        })
                            .catch(e => console.error('[contact] doUpdatePersonalOrg dexie mark error', e)),
                    ]);
                }
                const res = await this.doGetPersonalOrgContactByOrgId({ id: [orgId], _account });
                if (res.success && res.data) {
                    const personalOrgContactIdSet = new Set();
                    const personalContactIdSet = new Set();
                    Object.values(res.data).forEach(item => {
                        item.forEach(({ id: orgContactId, contactId }) => {
                            personalOrgContactIdSet.add(orgContactId);
                            personalContactIdSet.add(contactId);
                        });
                    });
                    const ids = [...personalOrgContactIdSet];
                    const contactIds = [...personalContactIdSet];
                    const list = [];
                    if (ids.length) {
                        list.push(Promise.all([
                            this.deleteOrgContactsByOrgIds([orgId], _account),
                            this.contactDB
                                .deleteData({
                                tableName: this.contactUtil.orgContactTable,
                                list: ids,
                                _account,
                            })
                                .catch(e => console.error('[contact] doUpdatePersonalOrg dexie orgContact delete error', e)),
                        ])
                            .then(() => this.handlePersonalOrgContactSyncRes({
                            idList: ids,
                            success: true,
                            action: 'delete',
                        }))
                            .catch(() => this.handlePersonalOrgContactSyncRes({
                            idList: ids,
                            success: false,
                            action: 'delete',
                        })));
                    }
                    if (contactIds.length) {
                        list.push(Promise.all([
                            this.updatePersonalOrgInLoki(contactIds, [orgId], 'delete', _account),
                            this.contactDB
                                .updateContactById({
                                list: contactIds.map(id => ({
                                    id,
                                    personalOrg: [orgId],
                                })),
                                action: 'delete',
                                _account,
                            })
                                .catch(e => console.error('[contact] doUpdatePersonalOrg dexie contact delete error', e)),
                        ])
                            .then(() => this.handlePersonalSyncRes({
                            idList: contactIds,
                            success: true,
                            action: 'update',
                        }))
                            .catch(() => this.handlePersonalSyncRes({
                            idList: contactIds,
                            success: false,
                            action: 'update',
                        })));
                    }
                    const arr = await Promise.all(list);
                    syncResArr = syncResArr.concat(arr);
                }
                const orgList = this.contactDB.personalOrgIntoOrg([data.personContactGroup]);
                const orgIdList = [this.transOrgIdByPersonalOrg(data.personContactGroup.groupId)];
                const promiseList = [];
                promiseList.push(Promise.all([
                    writeOrgsToLoki(this.getLokiApi(), orgList, this.getDbAccount(_account)),
                    this.contactDB
                        .intoTable({
                        tableName: this.contactUtil.orgTable,
                        list: orgList,
                        _account,
                    }, { asSoon: true })
                        .catch(e => console.error('[contact] doUpdatePersonalOrg dexie org error', e)),
                ])
                    .then(() => this.handlePersonalOrgSyncRes({
                    idList: orgIdList,
                    action: 'insert',
                    success: true,
                }))
                    .catch(() => this.handlePersonalOrgSyncRes({
                    idList: orgIdList,
                    action: 'insert',
                    success: false,
                })));
                if (data.accountIdList) {
                    const personalOrgContact = this.contactTrans.transPersonalOrgContact(data.accountIdList, orgIdList);
                    promiseList.push(Promise.all([
                        writeOrgContactsToLoki(this.getLokiApi(), personalOrgContact, this.getDbAccount(_account)),
                        this.contactDB
                            .intoTable({
                            tableName: this.contactUtil.orgContactTable,
                            list: personalOrgContact,
                            _account,
                        }, { asSoon: true })
                            .catch(e => console.error('[contact] doUpdatePersonalOrg dexie orgContact error', e)),
                    ])
                        .then(() => this.handlePersonalOrgContactSyncRes({
                        idList: personalOrgContact.map(oItem => oItem.id),
                        action: 'insert',
                        success: true,
                    }))
                        .catch(() => this.handlePersonalOrgContactSyncRes({
                        idList: personalOrgContact.map(oItem => oItem.id),
                        action: 'insert',
                        success: false,
                    })));
                    promiseList.push(Promise.all([
                        this.updatePersonalOrgInLoki(data.accountIdList, orgIdList, 'insert', _account),
                        this.contactDB
                            .updateContactById({
                            list: data.accountIdList.map(id => ({
                                id,
                                personalOrg: orgIdList,
                            })),
                            action: 'insert',
                            _account,
                        })
                            .catch(e => console.error('[contact] doUpdatePersonalOrg dexie contact error', e)),
                    ])
                        .then(() => this.handlePersonalSyncRes({
                        idList: data.accountIdList,
                        success: true,
                        action: 'update',
                    }))
                        .catch(() => this.handlePersonalSyncRes({
                        idList: data.accountIdList,
                        success: false,
                        action: 'update',
                    })));
                }
                const arr = await Promise.all(promiseList);
                syncResArr = syncResArr.concat(arr);
                const { flag, resData } = this.handleSyncRes(syncResArr);
                if (flag) {
                    this.contactDB.sendContactNotify(Object.assign(Object.assign({}, resData), { _account }));
                    return {
                        success: true,
                        data: {
                            id: orgId,
                            name,
                        },
                    };
                }
            }
            return {
                success: false,
            };
        }
        catch (e) {
            console.error('[contact] doUpdatePersonalOrg error', e);
            return {
                success: false,
            };
        }
    }
    async doGetPersonalOrgContactByOrgId(params) {
        const { id, _account } = params;
        try {
            const idList = util.singleToList(id);
            const lokiApi = this.getLokiApi();
            const dbAccount = this.getDbAccount(_account);
            const orgContactList = (await lokiApi.getByRangeCondition({
                dbName: contactSearchDbName,
                tableName: 'orgContact',
                adCondition: { type: 'anyOf', field: 'orgId', args: [idList] },
                _dbAccount: dbAccount,
            }));
            const data = {};
            (orgContactList || []).forEach(item => {
                const arr = data[item.orgId] || [];
                arr.push(item);
                data[item.orgId] = arr;
            });
            return {
                success: true,
                data,
            };
        }
        catch (e) {
            return {
                success: false,
            };
        }
    }
    async doInsertContactByPersonalOrgId(params) {
        const { orgIdList, idList, _account } = params;
        try {
            const notInPersonalOrg = orgIdList.some(item => !item.startsWith('personal_org'));
            if (notInPersonalOrg) {
                throw new Error('personal_org id not exits');
            }
            const { success, message } = await this.contactServer.insertPersonalContactByPersonalOrgId(params);
            if (success) {
                const personalOrgContact = this.contactTrans.transPersonalOrgContact(idList, orgIdList);
                const arr = await Promise.all([
                    Promise.all([
                        writeOrgContactsToLoki(this.getLokiApi(), personalOrgContact, this.getDbAccount(_account)),
                        this.contactDB
                            .intoTable({
                            tableName: this.contactUtil.orgContactTable,
                            list: personalOrgContact,
                            _account,
                        }, { asSoon: true })
                            .catch(e => console.error('[contact] doInsertContactByPersonalOrgId dexie orgContact error', e)),
                    ])
                        .then(() => this.handlePersonalOrgContactSyncRes({
                        success: true,
                        idList: personalOrgContact.map(item => item.id),
                        action: 'insert',
                    }))
                        .catch(() => this.handlePersonalOrgContactSyncRes({
                        success: false,
                        idList: personalOrgContact.map(item => item.id),
                        action: 'insert',
                    })),
                    Promise.all([
                        this.updatePersonalOrgInLoki(idList, orgIdList, 'insert', _account),
                        this.contactDB
                            .updateContactById({
                            list: idList.map(id => ({
                                id,
                                personalOrg: orgIdList,
                            })),
                            action: 'insert',
                            _account,
                        })
                            .catch(e => console.error('[contact] doInsertContactByPersonalOrgId dexie contact error', e)),
                    ])
                        .then(() => this.handlePersonalSyncRes({
                        success: true,
                        idList,
                        action: 'update',
                    }))
                        .catch(() => this.handlePersonalSyncRes({
                        success: false,
                        idList,
                        action: 'update',
                    })),
                ]);
                const { flag, resData } = this.handleSyncRes(arr);
                if (flag) {
                    this.contactDB.sendContactNotify(Object.assign(Object.assign({}, resData), { _account }));
                    return {
                        success: true,
                    };
                }
            }
            return {
                success: false,
                message,
            };
        }
        catch (e) {
            console.error('[contact] doInsertContactByPersonalOrgId error', e);
            return {
                success: false,
            };
        }
    }
    async doDeletePersonalOrg(params) {
        const { orgIdList, deletePersonContact, _account } = params;
        try {
            const notInPersonalOrg = orgIdList.some(item => !item.startsWith('personal_org'));
            if (notInPersonalOrg) {
                throw new Error('personal_org id not exits');
            }
            const res = await this.contactServer.deletePersonalOrg(params);
            if (res.success) {
                await Promise.all([
                    this.deletePersonalMarkFromLoki(params.orgIdList.map(item => (!item.startsWith('personal_org') ? `personal_org_${item}` : item)), _account),
                    this.contactDB
                        .doDeletePersonalMark({
                        ids: params.orgIdList.map(item => (!item.startsWith('personal_org') ? `personal_org_${item}` : item)),
                        _account,
                    })
                        .catch(e => console.error('[contact] doDeletePersonalOrg dexie mark error', e)),
                ]);
                const promiseList = [];
                const { success, data: personalOrgContactMap } = await this.doGetPersonalOrgContactByOrgId({ id: orgIdList, _account });
                const deleteOrgPromise = Promise.all([
                    this.deleteOrgsFromLoki(orgIdList, _account),
                    this.contactDB
                        .deleteData({
                        tableName: this.contactUtil.orgTable,
                        list: orgIdList,
                        _account,
                    })
                        .catch(e => console.error('[contact] doDeletePersonalOrg dexie org error', e)),
                ])
                    .then(() => this.handlePersonalOrgSyncRes({
                    success: true,
                    idList: orgIdList,
                    action: 'delete',
                }))
                    .catch(() => this.handlePersonalOrgSyncRes({
                    success: false,
                    idList: orgIdList,
                    action: 'delete',
                }));
                promiseList.push(deleteOrgPromise);
                if (success && personalOrgContactMap) {
                    const personalOrgIdSet = new Set();
                    const personalContactIdSet = new Set();
                    Object.values(personalOrgContactMap).forEach(item => {
                        item.forEach(({ id, contactId }) => {
                            personalOrgIdSet.add(id);
                            personalContactIdSet.add(contactId);
                        });
                    });
                    const orgContactIdList = [...personalOrgIdSet.keys()];
                    const contactIdList = [...personalContactIdSet.keys()];
                    if (orgContactIdList.length) {
                        const deleteOrgContact = Promise.all([
                            this.deleteOrgContactsByOrgIds(orgIdList, _account),
                            this.contactDB
                                .deleteData({
                                tableName: this.contactUtil.orgContactTable,
                                list: orgContactIdList,
                                _account,
                            })
                                .catch(e => console.error('[contact] doDeletePersonalOrg dexie orgContact error', e)),
                        ])
                            .then(() => this.handlePersonalOrgContactSyncRes({
                            idList: orgContactIdList,
                            success: true,
                            action: 'delete',
                        }))
                            .catch(() => this.handlePersonalOrgContactSyncRes({
                            idList: orgContactIdList,
                            success: false,
                            action: 'delete',
                        }));
                        promiseList.push(deleteOrgContact);
                    }
                    if (!deletePersonContact && contactIdList.length) {
                        const updateContact = Promise.all([
                            this.updatePersonalOrgInLoki(contactIdList, orgIdList, 'delete', _account),
                            this.contactDB
                                .updateContactById({
                                list: contactIdList.map(id => ({
                                    id,
                                    personalOrg: orgIdList,
                                })),
                                action: 'delete',
                                _account,
                            })
                                .catch(e => console.error('[contact] doDeletePersonalOrg dexie contact update error', e)),
                        ])
                            .then(() => this.handlePersonalSyncRes({
                            idList: contactIdList,
                            success: true,
                            action: 'update',
                        }))
                            .catch(() => this.handlePersonalSyncRes({
                            idList: contactIdList,
                            success: false,
                            action: 'update',
                        }));
                        promiseList.push(updateContact);
                    }
                    if (deletePersonContact && contactIdList.length) {
                        const deletePersonal = Promise.all([
                            this.deleteContactsFromLoki(contactIdList, _account),
                            this.contactDB
                                .personalContactDelete({
                                list: contactIdList,
                                _account,
                            })
                                .catch(e => console.error('[contact] doDeletePersonalOrg dexie contact delete error', e)),
                        ])
                            .then(() => this.handlePersonalSyncRes({
                            idList: contactIdList,
                            success: true,
                            action: 'delete',
                        }))
                            .catch(() => this.handlePersonalSyncRes({
                            idList: contactIdList,
                            success: false,
                            action: 'delete',
                        }));
                        promiseList.push(deletePersonal);
                    }
                }
                const arr = await Promise.all(promiseList);
                const { flag, resData } = this.handleSyncRes(arr);
                if (flag) {
                    this.contactDB.sendContactNotify(Object.assign(Object.assign({}, resData), { _account }));
                    return {
                        success: true,
                    };
                }
                return {
                    success: false,
                };
            }
            return {
                success: false,
                message: res.message === 'FA_GROUP_NOT_FOUND' ? '分组已被删除！' : res.message,
            };
        }
        catch (e) {
            console.error('[contact] doDeletePersonalOrg error', e);
            return {
                success: false,
            };
        }
    }
    async doInsertContact(params) {
        const { list: _list, _account } = params;
        try {
            let list = Array.isArray(_list) ? _list : [_list];
            if (!list.length) {
                return { success: false };
            }
            const needMark = list.some(item => item.isMark);
            if (needMark) {
                let { count: maxMarkedNum } = await this.getMaxPersonalMarkFromLoki(_account);
                list = list.map(item => {
                    if (item.isMark) {
                        item.marked = ++maxMarkedNum;
                    }
                    return item;
                });
            }
            const data = await this.contactServer.insertPersonalContact(params);
            if (needMark && (data === null || data === void 0 ? void 0 : data.success)) {
                const markList = data.data.map(item => {
                    var _a;
                    return ({
                        emails: item.email,
                        type: 1,
                        value: item.qiyeAccountId,
                        name: item.qiyeAccountName,
                        marked: (_a = item.marked) !== null && _a !== void 0 ? _a : 0,
                        _lastUpdateTime: Date.now(),
                    });
                });
                await Promise.all([
                    this.writePesonalMarkToLoki(markList, _account),
                    this.contactDB
                        .doInsertPersonalMark({ list: markList, _account }, { noNewMarkData: true })
                        .catch(e => console.error('[contact] doInsertContact dexie mark error', e)),
                ]);
            }
            return this.contactDB.doInsertOrReplacePersonal({ data: data, _account });
        }
        catch (e) {
            console.error('[contact] doInsertContact error', e);
            return {
                success: false,
            };
        }
    }
    async doUpdateContact(_params) {
        var _a;
        try {
            const { _account, params } = _params;
            params.groupIdList = (_a = params.groupIdList) === null || _a === void 0 ? void 0 : _a.filter(item => item && item.match(/\d/));
            const { count: maxMarkedNum, data: personalMarkList } = await this.getMaxPersonalMarkFromLoki(_account);
            const personamMarkMap = new Map(personalMarkList.map(item => [item.value, item.marked]));
            if (params.isMark === true && !personamMarkMap.has(params.accountId)) {
                params.marked = maxMarkedNum + 1;
            }
            if (!params.isMark && personamMarkMap.has(params.accountId)) {
                params.marked = -100;
            }
            if (params.isMark && personamMarkMap.has(params.accountId)) {
                params.marked = personamMarkMap.get(params.accountId);
            }
            Reflect.deleteProperty(params, 'isMark');
            const data = await this.contactServer.updatePersonalContact(_params);
            if (typeof params.marked === 'number' && params.marked > 0) {
                const list = this.contactTrans.transformPersonalParam2PersonalMarkEntityList(params);
                void Promise.all([
                    this.writePesonalMarkToLoki(list, _account),
                    this.contactDB.doInsertPersonalMark({ list, _account }).catch(e => console.error('[contact] doUpdateContact dexie mark error', e)),
                ]);
            }
            else if (params.marked === -100) {
                void Promise.all([
                    this.deletePersonalMarkFromLoki([params.accountId], _account),
                    this.contactDB.doDeletePersonalMark({ ids: [params.accountId], _account }).catch(e => console.error('[contact] doUpdateContact dexie mark delete error', e)),
                ]);
            }
            return this.contactDB.doInsertOrReplacePersonal({ data: data, _account });
        }
        catch (e) {
            console.error('[contact] doInsertContact error', e);
            return {
                success: false,
            };
        }
    }
    async doDeleteContact(params) {
        try {
            const data = await this.contactServer.deletePersonalContact(params);
            if (!data || !data.success) {
                return { success: false, reason: data === null || data === void 0 ? void 0 : data.message };
            }
            const { data: personalMarkList } = await this.getMaxPersonalMarkFromLoki(params._account);
            const personalMarkContactIds = personalMarkList.map(item => item.value);
            const needDeleteMarkIds = lodashIntersection(params.accountIdList, personalMarkContactIds);
            if (needDeleteMarkIds.length) {
                await Promise.all([
                    this.deletePersonalMarkFromLoki(needDeleteMarkIds, params._account),
                    this.contactDB
                        .doDeletePersonalMark({ ids: needDeleteMarkIds, _account: params._account })
                        .catch(e => console.error('[contact] doDeleteContact dexie mark error', e)),
                ]);
            }
            const deleteRes = {
                success: true,
                data: {
                    contact_personal: {
                        deleteDiff: params.accountIdList,
                    },
                    syncStatus: {
                        personal: true,
                    },
                },
            };
            const [_, deleteResult] = await Promise.all([
                this.deleteContactsFromLoki(params.accountIdList, params._account),
                this.contactDB.personalContactDelete({ list: params.accountIdList, _account: params._account }).catch(e => {
                    console.error('[contact] doDeleteContact dexie error', e);
                    return { success: true, data: deleteRes.data };
                }),
            ]);
            const resData = (deleteResult === null || deleteResult === void 0 ? void 0 : deleteResult.data) || deleteRes.data;
            if (resData) {
                this.contactDB.sendContactNotify(Object.assign(Object.assign({}, resData), this.contactUtil.getPublicParamFromMultipleOptions(params)));
            }
            return { success: true };
        }
        catch (e) {
            console.error('[contact] doDeleteContact error', e);
            return { success: false };
        }
    }
    async doBatchDeletePersonalMark(params) {
        var _a;
        const { configList, _account } = params;
        const operationList = configList.map(item => ({
            marked: -100,
            type: item.type === 1 ? '1' : '2',
            id: item.id,
        }));
        await this.contactServer.batchOperatePersonalMark({
            operateList: cloneDeep(operationList),
            _account: ((_a = this.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id) || '',
        });
        const contactIdMap = new Map();
        const orgIdMap = new Map();
        const ids = new Set();
        operationList.forEach(item => {
            if (item.type === '1') {
                contactIdMap.set(item.id, {
                    id: item.id,
                    marked: item.marked,
                });
            }
            else {
                orgIdMap.set(item.id, {
                    id: item.id,
                    marked: item.marked,
                });
            }
            ids.add(item.id);
        });
        await Promise.all([
            this.deletePersonalMarkFromLoki([...ids], _account),
            contactIdMap.size ? this.updateMarkedInLoki(contactIdMap, 1, _account) : Promise.resolve(),
            orgIdMap.size ? this.updateMarkedInLoki(orgIdMap, 2, _account) : Promise.resolve(),
            this.contactDB.doDeletePersonalMark({ ids: [...ids], _account }).catch(e => console.error('[contact] doBatchDeletePersonalMark dexie mark error', e)),
            contactIdMap.size
                ? this.contactDB
                    .updateContactOrgMarkInfoWithQuery({ ids: contactIdMap, type: 1, _account })
                    .catch(e => console.error('[contact] doBatchDeletePersonalMark dexie contact error', e))
                : Promise.resolve([]),
            orgIdMap.size
                ? this.contactDB
                    .updateContactOrgMarkInfoWithQuery({ ids: orgIdMap, type: 2, _account })
                    .catch(e => console.error('[contact] doBatchDeletePersonalMark dexie org error', e))
                : Promise.resolve([]),
        ]);
        return true;
    }
    async doBatchAddPersonalMark(params) {
        var _a;
        const { configList, _account } = params;
        const { count: baseMarkedValue, data: allMarkList } = await this.getMaxPersonalMarkFromLoki(_account);
        const operationList = new Map(configList.map((item, index) => [
            item.id,
            {
                marked: baseMarkedValue + 1 + index,
                type: item.type === 1 ? '1' : '2',
                id: item.id,
            },
        ]));
        const allMarkIdsSet = new Set(allMarkList.map(item => item.value));
        void configList.every(item => allMarkIdsSet.has(item.id));
        await this.contactServer.batchOperatePersonalMark({
            operateList: cloneDeep([...operationList.values()]),
            _account: ((_a = this.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id) || '',
        });
        const contactRequestIds = [];
        const orgIdRequestIds = [];
        operationList.forEach(item => {
            if (item.type === '1') {
                contactRequestIds.push(item.id);
            }
            else {
                orgIdRequestIds.push(item.id);
            }
        });
        const lokiApi = this.getLokiApi();
        const dbAccount = this.getDbAccount(_account);
        const [contactRows, orgModelList] = await Promise.all([
            contactRequestIds.length
                ? lokiApi.getByRangeCondition({
                    dbName: contactSearchDbName,
                    tableName: 'contact',
                    adCondition: { type: 'anyOf', field: 'id', args: [contactRequestIds] },
                    _dbAccount: dbAccount,
                })
                : Promise.resolve([]),
            orgIdRequestIds.length
                ? lokiApi.getByRangeCondition({
                    dbName: contactSearchDbName,
                    tableName: 'org',
                    adCondition: { type: 'anyOf', field: 'id', args: [orgIdRequestIds] },
                    _dbAccount: dbAccount,
                })
                : Promise.resolve([]),
        ]);
        const contactModelMap = new Map();
        const contactEmailsMap = new Map();
        (contactRows || []).forEach(row => {
            if (!contactModelMap.has(row.id)) {
                contactModelMap.set(row.id, row);
            }
            const emailSet = contactEmailsMap.get(row.id) || new Set();
            if (row.accountName) {
                emailSet.add(row.accountName);
            }
            (row.emails || []).forEach((e) => {
                if (e)
                    emailSet.add(e);
            });
            contactEmailsMap.set(row.id, emailSet);
        });
        const contactModelList = [...contactModelMap.values()];
        const markIntoList = new Map();
        contactEmailsMap.forEach((emailSet, contactId) => {
            markIntoList.set(contactId, {
                emails: [...emailSet],
                value: contactId,
                type: 1,
            });
        });
        contactModelList
            .filter(item => markIntoList.has(item.id))
            .forEach(item => {
            const markObjPart = markIntoList.get(item.id);
            markIntoList.set(item.id, Object.assign(Object.assign({}, markObjPart), { name: item.contactName, marked: operationList.get(item.id).marked, originId: item.id }));
        });
        orgModelList.forEach(item => {
            markIntoList.set(item.originId, {
                emails: [],
                value: item.id,
                type: 2,
                name: item.orgName,
                marked: operationList.get(item.id).marked,
                originId: item.originId,
            });
        });
        await Promise.all([
            this.writePesonalMarkToLoki([...markIntoList.values()].flat(), _account),
            contactModelList.length
                ? this.updateMarkedInLoki(new Map(contactModelList.map(item => [item.id, { id: item.id, marked: operationList.get(item.id).marked || 0 }])), 1, _account)
                : Promise.resolve(),
            orgModelList.length
                ? this.updateMarkedInLoki(new Map(orgModelList.map(item => [item.id, { id: item.id, marked: operationList.get(item.id).marked || 0 }])), 2, _account)
                : Promise.resolve(),
            this.contactDB
                .doInsertPersonalMark({
                list: [...markIntoList.values()].flat(),
                _account,
            }, { quickUpdate: true, isIncrease: true, noNewMarkData: false })
                .catch(e => console.error('[contact] doBatchAddPersonalMark dexie mark error', e)),
            contactModelList.length
                ? this.contactDB
                    .updateContactOrgMarkInfo({
                    list: contactModelList.map(item => (Object.assign(Object.assign({}, item), { marked: operationList.get(item.id).marked || 0 }))),
                    type: 1,
                    _account,
                })
                    .catch(e => console.error('[contact] doBatchAddPersonalMark dexie contact error', e))
                : Promise.resolve([]),
            orgModelList.length
                ? this.contactDB
                    .updateContactOrgMarkInfo({
                    list: orgModelList.map(item => (Object.assign(Object.assign({}, item), { marked: operationList.get(item.id).marked || 0 }))),
                    type: 2,
                    _account,
                })
                    .catch(e => console.error('[contact] doBatchAddPersonalMark dexie org error', e))
                : Promise.resolve([]),
        ]);
        return true;
    }
    async doBatchOperatePersonalMark(params, action = 'add') {
        const { configList, _account } = params;
        if (!Array.isArray(configList) || !configList.length) {
            return { success: false, msg: '传参不能为空' };
        }
        try {
            if (action === 'add') {
                await this.doBatchAddPersonalMark({
                    configList,
                    _account,
                });
            }
            else {
                await this.doBatchDeletePersonalMark(params);
            }
            return {
                success: true,
                msg: '',
            };
        }
        catch (ex) {
            console.error('[contact] doBatchOperatePersonalMark error', ex);
            return {
                success: false,
                msg: ex instanceof Error ? ex.message : `${ex}`,
            };
        }
    }
    async handlePersonalOrgSyncRes(params) {
        const { success, action, idList } = params;
        if (success) {
            const orgPersonal = {};
            if (action === 'delete') {
                orgPersonal.deleteDiff = idList;
            }
            else if (action === 'insert') {
                orgPersonal.insertDiff = idList;
            }
            else {
                orgPersonal.updateDiff = idList;
            }
            return {
                success: true,
                data: {
                    org_personal: orgPersonal,
                    syncStatus: {
                        personalOrg: true,
                    },
                },
            };
        }
        return {
            success: false,
        };
    }
    async handlePersonalOrgContactSyncRes(params) {
        const { success, action, idList } = params;
        if (success) {
            const orgContact_personal = {};
            if (action === 'delete') {
                orgContact_personal.deleteDiff = idList;
            }
            else if (action === 'insert') {
                orgContact_personal.insertDiff = idList;
            }
            else {
                orgContact_personal.updateDiff = idList;
            }
            return {
                success: true,
                data: {
                    orgContact_personal,
                    syncStatus: {
                        personalOrg: true,
                    },
                },
            };
        }
        return {
            success: false,
        };
    }
    async handlePersonalSyncRes(params) {
        const { success, action, idList } = params;
        if (success) {
            const contact_personal = {};
            if (action === 'delete') {
                contact_personal.deleteDiff = idList;
            }
            else if (action === 'insert') {
                contact_personal.insertDiff = idList;
            }
            else {
                contact_personal.updateDiff = idList;
            }
            return {
                success: true,
                data: {
                    contact_personal,
                    syncStatus: {
                        personal: true,
                    },
                },
            };
        }
        return {
            success: false,
        };
    }
    handleSyncRes(arr) {
        let resData = {};
        let flag = true;
        arr.forEach(item => {
            if (item.success && item.data && flag) {
                resData = merge(resData, item.data);
            }
            else {
                flag = false;
            }
        });
        return {
            resData,
            flag,
        };
    }
    async importPersonalContact(params) {
        var _a;
        try {
            const res = await this.contactServer.personContactImport(params);
            if (res.success && res.data) {
                await this.contactDB.doInsertOrReplacePersonal({
                    data: {
                        success: true,
                        data: res.data,
                    },
                    _account: ((_a = this.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id) || '',
                });
                return {
                    success: true,
                    data: res.data.length,
                };
            }
            return {
                success: false,
                code: res.code,
                message: res.message,
            };
        }
        catch (e) {
            console.error('[contact_personal_help] importPersonalContact error', e);
            return {
                success: false,
                message: this.contactUtil.catchError(e),
            };
        }
    }
    exportPersonalContact(params) {
        return this.contactServer.personContactExport(params);
    }
    exportPersonalContactTemplate(type) {
        return this.contactServer.personContactExportTemplate(type);
    }
}
export const ContactPersonalHelperInstance = new ContactPersonalHelper();
